import type { RenderContext, SectionProps } from "../context";
import { field } from "../context";
import { Container, Img, MultiLine, STACK, SectionHeader, SectionShell, cx } from "../ui";

type Item = SectionProps<"timeline">["node"]["content"]["items"][number];

function Marker({ i }: { i: number }) {
  return (
    <span aria-hidden="true" className="relative z-10 grid size-10 shrink-0 place-items-center rounded-full border-4 border-site-bg bg-site-primary text-sm font-semibold text-site-on-primary tabular-nums shadow-site-sm">
      {i + 1}
    </span>
  );
}

function Body({ ctx, item, i, align = "left" }: { ctx: RenderContext; item: Item; i: number; align?: "left" | "right" }) {
  const p = `content.items.${i}`;
  return (
    <div className={cx("rounded-site-md border border-site-border bg-site-surface p-5 shadow-site-sm @3xl:p-6", align === "right" && "@4xl:text-right")}>
      {item.label ? (
        <p className="mb-2 text-xs font-semibold tracking-[0.16em] text-site-primary uppercase" {...field(ctx, `${p}.label`)}>
          {item.label}
        </p>
      ) : null}
      <h3 className="baze-heading text-xl leading-snug @3xl:text-2xl" {...field(ctx, `${p}.title`)}>
        {item.title}
      </h3>
      <MultiLine ctx={ctx} text={item.description} path={`${p}.description`} className="mt-2 leading-relaxed text-site-muted" />
      {item.image ? (
        <div className="mt-5 aspect-[16/10] overflow-hidden rounded-site-sm bg-site-bg">
          <Img ctx={ctx} image={item.image} sizes="(min-width: 1024px) 480px, 100vw" path={`${p}.image`} className="transition-transform duration-700 hover:scale-[1.03] motion-reduce:transition-none" />
        </div>
      ) : null}
    </div>
  );
}

/** Ordered steps along a rail: a process, a company history, or a couple's story. */
export function Timeline({ node, ctx }: SectionProps<"timeline">) {
  const c = node.content;
  const items = c.items;

  if (node.variant === "alternating") {
    return (
      <SectionShell ctx={ctx} node={node}>
        <Container>
          <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} description={c.description} align="center" />
          <ol className={cx(STACK, "relative")}>
            <span aria-hidden="true" className="absolute top-0 bottom-0 left-5 w-px bg-site-border @4xl:left-1/2 @4xl:-translate-x-1/2" />
            <span aria-hidden="true" className="baze-draw-y absolute top-0 bottom-0 left-5 w-px bg-site-primary @4xl:left-1/2 @4xl:-translate-x-1/2" />
            {items.map((item, i) => {
              const right = i % 2 === 1;
              return (
                <li key={i} className="relative grid grid-cols-[2.5rem_1fr] gap-4 pb-10 last:pb-0 @4xl:grid-cols-[1fr_2.5rem_1fr] @4xl:gap-8">
                  <div className="@4xl:col-start-2 @4xl:row-start-1 @4xl:flex @4xl:justify-center">
                    <Marker i={i} />
                  </div>
                  <div className={cx(right ? "baze-reveal-left @4xl:col-start-3" : "baze-reveal-right @4xl:col-start-1", "@4xl:row-start-1")}>
                    <Body ctx={ctx} item={item} i={i} align={right ? "left" : "right"} />
                  </div>
                </li>
              );
            })}
          </ol>
        </Container>
      </SectionShell>
    );
  }

  return (
    <SectionShell ctx={ctx} node={node}>
      <Container>
        <div className="grid gap-10 @4xl:grid-cols-[1fr_1.6fr] @4xl:gap-16">
          <div className="@4xl:sticky @4xl:top-[calc(7rem+var(--baze-sticky-top,0px))] @4xl:self-start">
            <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} description={c.description} />
          </div>
          <ol className="relative">
            <span aria-hidden="true" className="absolute top-2 bottom-2 left-5 w-px bg-site-border" />
            <span aria-hidden="true" className="baze-draw-y absolute top-2 bottom-2 left-5 w-px bg-site-primary" />
            {items.map((item, i) => (
              <li key={i} className="baze-reveal relative grid grid-cols-[2.5rem_1fr] gap-4 pb-8 last:pb-0 @3xl:gap-6">
                <Marker i={i} />
                <Body ctx={ctx} item={item} i={i} />
              </li>
            ))}
          </ol>
        </div>
      </Container>
    </SectionShell>
  );
}
